/**
 * Stats → Overview → Listing history (A7Y-5020).
 *
 * Two freshness levels share this table. The totals come from live Firestore `count()` aggregations; the
 * per-app counts, the analysis join and the failed-attempt sub-counts come from the nightly snapshot
 * (`snapshot`, null until the job has written one on this plane). A figure that needs the snapshot reads "—"
 * with its reason when there is none — never 0 — and the snapshot's own `computedAt` is shown beside the
 * live `generatedAt` so a stale figure is visibly stale.
 */
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@a7y/tailwind-catalyst';
import type { StatsOverviewBlockMeta, StatsOverviewListingHistory } from '@a7y/types';
import { formatInt, formatUtcInstant } from '../format';
import { BlockMetaLine } from './OverviewBlockPanel';
import { Footnote, StatRow } from './parts';

export const NO_SNAPSHOT_REASON = 'no nightly snapshot on this plane yet';
const NO_SNAPSHOT_VALUE = '—';

export function ListingHistoryBlock({
  body,
  meta,
}: {
  body: StatsOverviewListingHistory;
  meta: StatsOverviewBlockMeta;
}) {
  const snap = body.snapshot;
  const versionMismatch =
    snap !== null &&
    (snap.builderVersion !== body.builderVersion || snap.analysisVersion !== body.analysisVersion);
  const apps = (n: number | undefined, what: string) =>
    n === undefined ? `${what}: ${NO_SNAPSHOT_REASON}` : `across ${formatInt(n)} apps`;

  return (
    <div>
      <Table dense striped data-testid="listing-history-table">
        <TableHead>
          <TableRow>
            <TableHeader>Line</TableHeader>
            <TableHeader>Count</TableHeader>
            <TableHeader>Detail</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <StatRow
            testId="lh-observations"
            label="Archived listing captures"
            value={formatInt(body.observations.total)}
            detail={apps(snap?.observationApps, 'app count')}
          />
          <StatRow
            testId="lh-observations-wayback"
            indent
            label="Wayback"
            value={formatInt(body.observations.wayback)}
            detail="includes captures written before the source was recorded"
          />
          <StatRow
            testId="lh-observations-commoncrawl"
            indent
            label="Common Crawl"
            value={formatInt(body.observations.commoncrawl)}
          />
          <StatRow
            testId="lh-backfill"
            label="Apps backfilled, by status"
            value={formatInt(body.backfill.total)}
            detail={`${formatInt(body.backfill.complete)} complete, ${formatInt(body.backfill.failed)} failed, ${formatInt(body.backfill.pending)} pending, ${formatInt(body.backfill.running)} running`}
          />
          <StatRow
            testId="lh-live-fetch"
            label="Daily live-fetch observations"
            value={formatInt(body.liveFetchObservations)}
          />
          <StatRow testId="lh-snapshots" label="Listing snapshots" value={formatInt(body.listingSnapshots)} />
          <StatRow testId="lh-live-listings" label="Live app listings" value={formatInt(body.liveAppListings)} />
          <StatRow
            testId="lh-states"
            label="Folded listing states, current builder version"
            value={formatInt(body.states.currentVersion)}
            detail={`${apps(snap?.stateAppsCurrentVersion, 'app count')}; ${formatInt(body.states.allVersions)} all versions`}
          />
          <StatRow
            testId="lh-changesets"
            label="Changesets, current builder version"
            value={formatInt(body.changesets.currentVersion)}
            detail={`${apps(snap?.changesetAppsCurrentVersion, 'app count')}; ${formatInt(body.changesets.allVersions)} all versions; ${formatInt(body.changesets.copy)} copy, ${formatInt(body.changesets.creative)} creative, ${formatInt(body.changesets.releaseOnly)} release-only`}
          />
          <StatRow
            testId="lh-analyses"
            label="AI changeset analyses (records, incl. failed attempts)"
            value={formatInt(body.analyses.total)}
            detail={`${formatInt(body.analyses.currentVersion)} on the current analysis version`}
          />
          <StatRow
            testId="lh-analyses-failed"
            indent
            label="of which failed attempts (snapshot)"
            value={
              snap
                ? `${formatInt(snap.analysisFailureRecords.total)} / ${formatInt(snap.analysisFailureRecords.currentVersion)}`
                : NO_SNAPSHOT_VALUE
            }
            detail={
              snap
                ? 'all versions / current version, counted by the nightly snapshot'
                : NO_SNAPSHOT_REASON
            }
          />
          <StatRow
            testId="lh-analysis-join"
            label="Current-version changesets with a successful current-version analysis"
            value={snap ? formatInt(snap.changesetsCurrentVersionWithCurrentAnalysis) : NO_SNAPSHOT_VALUE}
            detail={
              snap
                ? `of ${formatInt(body.changesets.currentVersion)} current-version changesets`
                : NO_SNAPSHOT_REASON
            }
          />
        </TableBody>
      </Table>
      <BlockMetaLine meta={meta} testId="lh-meta" />
      <Footnote testId="lh-snapshot-line">
        {snap
          ? `Per-app, failed-attempt and analysis-join figures come from the nightly snapshot of ${formatUtcInstant(snap.computedAt)}.`
          : `Per-app, failed-attempt and analysis-join figures need the nightly snapshot; ${NO_SNAPSHOT_REASON}.`}
      </Footnote>
      {snap && versionMismatch && (
        <Footnote testId="lh-version-mismatch">
          The snapshot was folded against builder version {snap.builderVersion} and analysis version{' '}
          {snap.analysisVersion}; the live counts use {body.builderVersion} and {body.analysisVersion}, so its
          per-app and join figures describe a different version until the next nightly run.
        </Footnote>
      )}
      <Footnote testId="lh-records-note">
        Analysis counts are records: a failed attempt is a record too. The join counts a changeset only when a
        successful current-version analysis exists for it.
      </Footnote>
    </div>
  );
}
